import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Panel, StatTile, Empty, fmt, pct, shortDate } from "./kit";
import { ShotZones, zonesFromSeason } from "./ShotZones";

const SHOOT = [["FG%", "fg_pct"], ["3P%", "fg3_pct"], ["FT%", "ft_pct"], ["eFG%", "efg_pct"], ["TS%", "ts_pct"]];
const TIP = { contentStyle: { background: "#0F172A", border: "1px solid #1E293B", borderRadius: 12, color: "#F8FAFC" } };

const ShootingBars = ({ s }) => (
  <div className="space-y-3">
    {SHOOT.map(([label, k]) => (
      <div key={k} data-testid={`shooting-${k}`}>
        <div className="mb-1 flex justify-between font-mono-gd text-xs uppercase tracking-wider">
          <span className="text-slate-400">{label}</span><span className="text-white">{pct(s[k])}</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-[#162032]">
          <div className="h-full rounded-full bg-[#39FF14]" style={{ width: `${Math.min(100, Number(s[k] ?? 0))}%` }} />
        </div>
      </div>
    ))}
  </div>
);

const GameLog = ({ games }) => (
  <div className="overflow-x-auto">
    <table data-testid="per-game-table" className="w-full min-w-[480px] text-left text-sm">
      <thead><tr className="font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">
        {["Date", "Opponent", "PTS", "REB", "AST", "EFF"].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}
      </tr></thead>
      <tbody>
        {games.map((g, i) => (
          <tr key={g.game_id ?? i} className="border-t border-[#1E293B] font-mono-gd text-slate-200">
            <td className="px-2 py-2">{shortDate(g.date)}</td><td className="px-2 py-2 font-sans">{g.opponent ?? "—"}</td>
            <td className="px-2 py-2 text-[#39FF14]">{fmt(g.pts)}</td><td className="px-2 py-2">{fmt(g.reb)}</td>
            <td className="px-2 py-2">{fmt(g.ast)}</td><td className="px-2 py-2">{fmt(g.eff)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export function StatsPanel({ season, perGame, zonePct }) {
  const s = season ?? {};
  const games = Array.isArray(perGame) ? perGame : Array.isArray(s.per_game) ? s.per_game : [];
  const zones = zonePct ?? zonesFromSeason(s.zones);
  if (!s.gp && !games.length) return <Empty title="No games logged" text="Stats appear here once games are recorded in the GAMEDAY app." />;
  const chart = games.map((g, i) => ({ name: g.opponent ?? `G${i + 1}`, PTS: g.pts ?? 0, REB: g.reb ?? 0, AST: g.ast ?? 0 }));
  return (
    <div className="space-y-6" data-testid="stats-panel">
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
        {[["PPG", "ppg", true], ["RPG", "rpg"], ["APG", "apg"], ["SPG", "spg"], ["BPG", "bpg"], ["MPG", "mpg"]].map(([l, k, a]) => (
          <StatTile key={k} testid={`stat-${k}`} label={l} value={fmt(s[k])} accent={a} />
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel>
          <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Shooting</h3>
          <ShootingBars s={s} />
          <div className="mt-5 grid grid-cols-3 gap-3">
            <StatTile label="GP" value={fmt(s.gp)} /><StatTile label="EFF" value={fmt(s.eff)} /><StatTile label="AST/TO" value={fmt(s.ast_to)} />
          </div>
        </Panel>
        <Panel>
          <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Shot zones</h3>
          <ShotZones zones={zones} />
        </Panel>
      </div>
      {games.length > 0 && (
        <Panel>
          <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Game by game</h3>
          <div className="h-56 w-full">
            <ResponsiveContainer>
              <BarChart data={chart}>
                <CartesianGrid stroke="#1E293B" vertical={false} />
                <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} tickLine={false} />
                <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} width={28} />
                <Tooltip {...TIP} cursor={{ fill: "rgba(57,255,20,0.06)" }} />
                <Bar dataKey="PTS" fill="#39FF14" radius={[4, 4, 0, 0]} />
                <Bar dataKey="REB" fill="#00E5FF" radius={[4, 4, 0, 0]} />
                <Bar dataKey="AST" fill="#64748B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4"><GameLog games={games} /></div>
        </Panel>
      )}
    </div>
  );
}
